import React, { useRef, useState, useEffect, useCallback } from 'react';

const CRAYON_COLORS = ['#1a1a1a', '#c62828', '#2e7d32', '#1565c0', '#f9a825', '#6a1b9a', '#e65100'];

export default function ScribbleCanvas({ active, onClose }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState('#1a1a1a');
  const [lineWidth, setLineWidth] = useState(3);
  const lastPoint = useRef(null);

  // Resize canvas to window
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      // Save existing drawing
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      tempCanvas.getContext('2d').drawImage(canvas, 0, 0);

      canvas.width = window.innerWidth;
      canvas.height = document.documentElement.scrollHeight;

      // Restore drawing
      const ctx = canvas.getContext('2d');
      ctx.drawImage(tempCanvas, 0, 0);
    };

    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [active]);

  // Crayon-style draw with jitter
  const drawCrayon = useCallback((ctx, from, to) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const steps = Math.max(1, Math.floor(dist / 2));

    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      const x = from.x + dx * t;
      const y = from.y + dy * t;

      // Multiple offset strokes for crayon texture
      for (let j = 0; j < 3; j++) {
        const jx = (Math.random() - 0.5) * lineWidth * 0.8;
        const jy = (Math.random() - 0.5) * lineWidth * 0.8;
        const alpha = 0.3 + Math.random() * 0.4;

        ctx.beginPath();
        ctx.arc(x + jx, y + jy, lineWidth * (0.3 + Math.random() * 0.4), 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.globalAlpha = alpha;
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }, [color, lineWidth]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top + window.scrollY,
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    lastPoint.current = getPos(e);
  };

  const draw = (e) => {
    if (!isDrawing || !canvasRef.current) return;
    e.preventDefault();
    const pos = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    drawCrayon(ctx, lastPoint.current, pos);
    lastPoint.current = pos;
  };

  const stopDraw = () => {
    setIsDrawing(false);
    lastPoint.current = null;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  if (!active) return null;

  return (
    <div className="scribble-canvas-wrapper">
      <canvas
        ref={canvasRef}
        className="scribble-canvas"
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={stopDraw}
        onMouseLeave={stopDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={stopDraw}
      />

      {/* Toolbar */}
      <div className="scribble-toolbar">
        <div className="scribble-toolbar-inner">
          {/* Color swatches */}
          <div className="scribble-colors">
            {CRAYON_COLORS.map((c) => (
              <button
                key={c}
                className={`scribble-color-btn ${color === c ? 'active' : ''}`}
                style={{ background: c }}
                onClick={() => setColor(c)}
                title={c}
              />
            ))}
          </div>

          {/* Size */}
          <div className="scribble-sizes">
            {[2, 4, 8].map((w) => (
              <button
                key={w}
                className={`scribble-size-btn ${lineWidth === w ? 'active' : ''}`}
                onClick={() => setLineWidth(w)}
              >
                <svg viewBox="0 0 20 20" width="20" height="20">
                  <circle cx="10" cy="10" r={w} fill="#1a1a1a" />
                </svg>
              </button>
            ))}
          </div>

          {/* Actions */}
          <button className="scribble-action-btn" onClick={clearCanvas}>
            CLEAR
          </button>
          <button className="scribble-action-btn scribble-close-btn" onClick={onClose}>
            DONE ✓
          </button>
        </div>

        {/* Hand-drawn toolbar border */}
        <svg className="scribble-toolbar-border" viewBox="0 0 500 70" preserveAspectRatio="none">
          <path
            d="M 5,5 Q 3,2 10,3 L 490,6 Q 498,5 496,14 L 494,58 Q 496,67 486,65 L 14,63 Q 4,65 6,55 Z"
            fill="#f5f3ee"
            stroke="#1a1a1a"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}
